'use client';

import {
  CREATOR_PLANS,
  PLAN_ENTITLEMENTS,
  PLAN_NAME,
  PlanCode,
  SourceType,
  entitlementsFor,
  exposureWeight,
  type ExposureCounts,
} from '@content-removal/shared';
import type { OverviewLink } from './live-overview';

/** Four levels over the same weighting the loss estimate uses. */
function level(exposure: ExposureCounts): number {
  const weight = exposureWeight(exposure);
  return weight >= 20 ? 4 : weight >= 8 ? 3 : weight >= 3 ? 2 : weight > 0 ? 1 : 0;
}

const LEVELS = ['None', 'Low', 'Medium', 'High', 'Severe'];

export function LossCard({
  low,
  high,
  exposure,
  profileHref,
  Link,
}: {
  low: number;
  high: number;
  exposure: ExposureCounts;
  profileHref: string;
  Link: OverviewLink;
}) {
  const on = level(exposure);
  const usd = (n: number) => `$${n.toLocaleString('en-US')}`;
  return (
    <div className="ov-card ov-loss">
      <h3>Estimate loss</h3>
      <p className="ov-cap">Monthly revenue at risk while these pages stay up.</p>
      {high > 0 ? (
        <p className="ov-fig">
          {usd(low)}–{usd(high)}
          <small> / month</small>
        </p>
      ) : (
        // No audience size yet: $0 would read as nothing at risk.
        <p className="ov-cap">
          <Link href={profileHref}>Add your audience size</Link> to see what these pages put at risk.
        </p>
      )}
      <div className="ov-threat">
        <div className="ov-threat-h"><span>Exposure</span><b>{LEVELS[on]}</b></div>
        <div className="ov-segs">{[1, 2, 3, 4].map((n) => <i key={n} className={n <= on ? 'is-on' : ''} />)}</div>
        <div className="ov-segs-l"><span>Low</span><span>Medium</span><span>High</span><span>Severe</span></div>
      </div>
      <p className="ov-note">Based on your audience size and what we found. An estimate, not actual lost revenue.</p>
    </div>
  );
}

const OVERVIEW_ICON = {
  search: <path d="M10.2 10.2L13.5 13.5M11.2 7a4.2 4.2 0 1 1-8.4 0 4.2 4.2 0 0 1 8.4 0z" />,
  tube: <path d="M2.5 5a1.5 1.5 0 0 1 1.5-1.5h8A1.5 1.5 0 0 1 13.5 5v6a1.5 1.5 0 0 1-1.5 1.5H4A1.5 1.5 0 0 1 2.5 11zM6.8 6.3v3.4L9.7 8z" />,
  social: <path d="M7.5 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM12.6 6.5a1.6 1.6 0 1 1-3.2 0 1.6 1.6 0 0 1 3.2 0zM2 12.5c.6-2 2-3 3.5-3s2.9 1 3.5 3M9.4 12c.4-1.3 1.2-2 2.3-2 1 0 1.8.6 2.3 1.8" />,
  forum: <path d="M3 4h10v6H7l-3 2.5V10H3z" />,
  messenger: <path d="M13.5 3L2.5 7.5l4 1.5 1.5 4 5.5-10zM6.5 9l3-2.5" />,
  lock: <path d="M4 7.5h8v6H4zM5.8 7.5V5.6a2.2 2.2 0 0 1 4.4 0v1.9" />,
};

const SOURCES: Array<{ source: string; icon: keyof typeof OVERVIEW_ICON; tone: Tone; name: string; what: (sites: number) => string }> = [
  { source: SourceType.SEARCH, tone: 'blue', icon: 'search', name: 'Search engines', what: () => 'Google, Google Images, Bing, Yandex' },
  { source: SourceType.TUBE, tone: 'pink', icon: 'tube', name: 'Leak and tube sites', what: (sites) => (sites ? `${sites.toLocaleString('en-US')} known site${sites === 1 ? '' : 's'}, page by page` : 'Every site we know, page by page') },
  { source: SourceType.SOCIAL, tone: 'orange', icon: 'social', name: 'Social platforms', what: () => 'Accounts using your photos or name' },
  { source: SourceType.FORUM, tone: 'green', icon: 'forum', name: 'Forums and boards', what: () => 'Threads that share or trade your content' },
  { source: SourceType.MESSENGER, tone: 'purple', icon: 'messenger', name: 'Telegram and messengers', what: () => 'Public channels and groups' },
];

/** The cheapest plan that searches a source, for the badge on a locked row. */
function firstPlanWith(source: string): PlanCode {
  return CREATOR_PLANS.find((code) => (PLAN_ENTITLEMENTS[code].sources as string[]).includes(source)) ?? PlanCode.PREMIUM;
}

/** Every profile is searched once, when it is added: no plan repeats it today. */
function cadence(): string {
  return 'once';
}

type Tone = 'blue' | 'pink' | 'orange' | 'green' | 'purple' | 'teal';

function Svg({ name, tone }: { name: keyof typeof OVERVIEW_ICON; tone?: Tone }) {
  return <span className={`ov-pn${tone ? ` tone-${tone}` : ''}`}><svg viewBox="0 0 16 16" aria-hidden="true">{OVERVIEW_ICON[name]}</svg></span>;
}

export function HowCard({
  plan,
  paying,
  sites,
  onUnlock,
}: {
  plan: PlanCode | null;
  paying: boolean;
  /** Sites the search walks, from the search itself. */
  sites: number;
  onUnlock: () => void;
}) {
  const sources = entitlementsFor(plan).sources as string[];
  const every = cadence();
  return (
    <div className="ov-card ov-how">
      <h3>How we protect you</h3>
      <p className="ov-cap">What we search, and what happens to every case.</p>
      <span className="ov-flow-t">What we search</span>
      <ul className="ov-path ov-srcs">
        {SOURCES.map((row) => {
          // Before a plan the first search looks everywhere once.
          const has = !paying || sources.includes(row.source);
          const needs = firstPlanWith(row.source);
          return (
            <li key={row.source} className={`ov-ps${has ? '' : ' is-locked'}`}>
              <Svg name={has ? row.icon : 'lock'} tone={has ? row.tone : undefined} />
              <div><b>{row.name}</b><span>{row.what(sites)}</span></div>
              {has ? <em>{every}</em> : <button type="button" className="ov-plan-b" onClick={onUnlock}>{PLAN_NAME[needs]}</button>}
            </li>
          );
        })}
      </ul>
      <span className="ov-flow-t">What happens to every case</span>
      <ol className="ov-path">
        <li className="ov-ps"><Svg name="search" tone="blue" /><div><b>Found</b><span>We find the page and open a case for the site</span></div></li>
        <li className="ov-ps"><Svg name="forum" tone="orange" /><div><b>Processing</b><span>Our team finds who answers for it and sends the notices</span></div></li>
        <li className="ov-ps"><Svg name="search" tone="purple" /><div><b>Delisting requested</b><span>Our team asks Google to drop it from results</span></div></li>
        <li className="ov-ps is-ok"><Svg name="tube" tone="green" /><div><b>Removed</b><span>The page is down. Timing depends on the site, host and search engine</span></div></li>
        <li className="ov-ps is-ok"><Svg name="social" tone="teal" /><div><b>Re-checked</b><span>Removed pages are re-checked, and anything that comes back is flagged so our team can file again</span></div></li>
      </ol>
      <div className="ov-loop">
        <span className="ov-loop-t">If a page comes back</span>
        <ol className="ov-path">
          <li className="ov-ps is-alert"><Svg name="tube" /><div><b>Back</b><span>The re-check of removed pages flags it</span></div></li>
          <li className="ov-ps is-again"><Svg name="forum" /><div><b>Filed again</b><span>Our team sends the notices again</span></div></li>
        </ol>
      </div>
    </div>
  );
}
